import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Subject, finalize, merge} from 'rxjs';
import {debounceTime, map, switchMap, takeUntil} from 'rxjs/operators';
import {FieldFilter} from '@6grain/masofa-monolith-api-client/dictionaries';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {MapControllerInput, MapInitOptions} from '@components/openstreetmap/interfaces';
import {GisPolygon} from '@components/openstreetmap/interfaces/gis-component.interfaces';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {environment} from '@root/src/environments/environment';
import {
  AnomalyMapManagerService,
  ANOMALY_MAP_POINT_BUFFER_METERS,
  anomalyApproxCircleRing,
  isAnomalyPointGeometry,
} from '@app/pages/crop-monitoring/pages/anomalies/anomaly-map-manager.service';
import {
  ANOMALY_FIELD_OUTLINE_STROKE,
  ANOMALY_LEGEND_ITEMS,
  ANOMALY_RECURRING_STROKE_DASH,
  isRecurringAnomalyDetail,
  ANOMALY_TYPE_COLOR_BY_ENUM,
  resolveAnomalyPolygonColor,
} from '@app/pages/crop-monitoring/pages/anomalies/anomaly-type-colors';
import {
  BID_MAP_ZOOM_DETAIL_MIN,
  bidMapZoomBand,
  type BidMapZoomBand,
} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-zoom.config';
import {
  getBidMapBounds,
  latLngRingToBounds,
  padLatLngBounds,
  type BidMapLatLngBounds,
} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-bounds.util';
import {AnomalyDateSyncManager} from '@app/pages/crop-monitoring/pages/anomalies/managers/anomaly-date-sync.manager';
import {SatelliteProductsService} from '@services/satellite-products.service';
import {AnomalyDateIndicatorComponent} from '@app/pages/crop-monitoring/pages/anomalies/components/anomaly-date-indicator/anomaly-date-indicator.component';

export type GisAnomaliesInitialMapViewport = {
  lat: number;
  lng: number;
  zoom?: number;
};

@Component({
  selector: 'app-gis-anomalies',
  standalone: true,
  imports: [CommonModule, TranslateModule, OpenStreetMapComponent, AnomalyDateIndicatorComponent],
  templateUrl: './gis-anomalies.html',
  styleUrl: './gis-anomalies.scss',
})
export class GisAnomaliesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() filtersParams: FieldFilter[] = [];
  /** When set (e.g. field passport), first map open centers here before viewport reload. */
  @Input() initialMapViewport: GisAnomaliesInitialMapViewport | null = null;

  @ViewChild(OpenStreetMapComponent) openstreetmapComponent!: OpenStreetMapComponent;

  private readonly anomalyMapManager = inject(AnomalyMapManagerService);
  private readonly regionManager = inject(RegionManagerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly satelliteProducts = inject(SatelliteProductsService);

  readonly anomalyDateSync = new AnomalyDateSyncManager(() => ({
    openstreetmapComponent: this.openstreetmapComponent,
    satelliteProducts: this.satelliteProducts,
  }));

  regionPathDisplay = '';

  private readonly destroy$ = new Subject<void>();
  private readonly cancelRequest$ = new Subject<void>();
  private readonly mapViewportCancel$ = merge(this.cancelRequest$, this.destroy$);
  private mapViewportEventsBound = false;
  private regionsLoaded = false;
  mapZoom = 6;
  private previousZoomBand: BidMapZoomBand | null = null;
  anomaliesCount = 0;
  private lastCountFiltersHash = '';
  private pendingCountFiltersHash = '';

  mapController$ = new Subject<MapControllerInput>();

  readonly anomalyLegendItems = ANOMALY_LEGEND_ITEMS;
  readonly anomalyTypeColors = ANOMALY_TYPE_COLOR_BY_ENUM;
  readonly fieldOutlineLegendColor = ANOMALY_FIELD_OUTLINE_STROKE;

  mapOptions: MapInitOptions = {
    height: 'calc(100vh - 270px)',
    center: [environment.map.lat, environment.map.lng],
    zoom: 6,
    showControls: true,
    minimalControls: false,
    drawingMode: false,
    showDrawingControls: true,
    baseMap: 'osm',
    showRegionNames: false,
    disableBounds: false,
  };

  private anomalyPolygonIds: string[] = [];
  private anomalyMarkerIds: string[] = [];
  private pendingFocusedAnomalyId: string | null = null;
  /** One-shot bbox so deep-link focus loads details around the target, not Uzbekistan. */
  private pendingFocusBounds: BidMapLatLngBounds | null = null;
  private ignoreNextZoomReload = false;
  private initialViewportApplied = false;

  ngOnInit(): void {
    this.applyInitialMapViewportToOptions();
    if (!this.initialMapViewport) {
      this.mapOptions.center = [environment.map.lat, environment.map.lng];
    }
    this.regionPathDisplay = this.getRegionPathString();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.regionPathDisplay = this.getRegionPathString();
        this.captureOpenedAnomalyPopup();
        this.scheduleReload(false);
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialMapViewport']) {
      this.applyInitialMapViewportToOptions();
      if (!changes['initialMapViewport'].firstChange && this.isMapReady()) {
        this.initialViewportApplied = false;
        this.tryApplyInitialMapViewport();
      }
    }
    if (changes['filtersParams'] && !changes['filtersParams'].firstChange) {
      this.scheduleReload(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.anomalyDateSync.destroy();
  }

  onMapReady(): void {
    this.syncZoomFromMap();
    this.tryApplyInitialMapViewport();
    this.loadRegionsOnce();
    if (!this.mapViewportEventsBound) {
      this.bindMapViewportEvents();
    }
    setTimeout(() => this.mapController$.next({invalidateSize: true}), 200);
  }

  isMapReady(): boolean {
    return !!(
      this.openstreetmapComponent?.isMapInitialized &&
      this.openstreetmapComponent.getLayersController?.()
    );
  }

  private applyInitialMapViewportToOptions(): void {
    const viewport = this.initialMapViewport;
    if (!viewport) {
      return;
    }
    const lat = Number(viewport.lat);
    const lng = Number(viewport.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return;
    }
    const zoom = viewport.zoom ?? BID_MAP_ZOOM_DETAIL_MIN;
    this.mapOptions.center = [lat, lng];
    this.mapOptions.zoom = zoom;
    this.mapZoom = zoom;
    this.previousZoomBand = bidMapZoomBand(zoom);
  }

  /** Field-scoped hosts (passport) open centered on the parcel, not the country default. */
  private tryApplyInitialMapViewport(): boolean {
    if (this.initialViewportApplied || !this.initialMapViewport || !this.isMapReady()) {
      return false;
    }
    const lat = Number(this.initialMapViewport.lat);
    const lng = Number(this.initialMapViewport.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return false;
    }
    const zoom = this.initialMapViewport.zoom ?? BID_MAP_ZOOM_DETAIL_MIN;
    const lc = this.openstreetmapComponent.getLayersController();
    if (!lc) {
      return false;
    }

    this.ignoreNextZoomReload = true;
    this.mapZoom = zoom;
    this.previousZoomBand = bidMapZoomBand(zoom);
    lc.centerToPoint(lat, lng, zoom, false);
    this.pendingFocusBounds = padLatLngBounds(
      {
        minLat: lat,
        maxLat: lat,
        minLng: lng,
        maxLng: lng,
      },
      0.02,
    );
    this.initialViewportApplied = true;
    this.scheduleReload(false);
    return true;
  }

  /**
   * Center on a list/deep-link anomaly and load details around it.
   * Returns false when the map is not ready (caller should retry).
   */
  focusAnomalyRow(row: any): boolean {
    if (!this.isMapReady()) {
      return false;
    }
    const osm = this.openstreetmapComponent;
    const lc = osm.getLayersController();
    const ap = row?.anomalyPolygon ?? row;
    const focusId = this.readAnomalyId(ap);
    this.pendingFocusedAnomalyId = focusId;
    const pjRaw =
      typeof ap?.polygonJson === 'string'
        ? ap.polygonJson.trim()
        : ap?.polygonJson
          ? JSON.stringify(ap.polygonJson)
          : '';
    const lat = ap.lat != null ? Number(ap.lat) : NaN;
    const lng = ap.lng != null ? Number(ap.lng) : NaN;
    const hasPoint = !Number.isNaN(lat) && !Number.isNaN(lng);
    if (!lc || (!pjRaw && !hasPoint)) {
      return false;
    }
    try {
      lc.removeDynamicPolygon('anomaly-highlight');
      lc.removeDynamicPolygon('anomaly-highlight-field');

      const color = resolveAnomalyPolygonColor(ap);
      const coordinates =
        pjRaw && !isAnomalyPointGeometry(pjRaw)
          ? PolygonConverterUtil.polygonJsonToCoordinates(pjRaw)
          : [];
      const hasPolygon = coordinates.length >= 3;
      const ring = hasPolygon
        ? coordinates
        : hasPoint
          ? anomalyApproxCircleRing(lat, lng, ANOMALY_MAP_POINT_BUFFER_METERS)
          : [];
      if (ring.length < 3) {
        return false;
      }

      const center = PolygonConverterUtil.getPolygonCenter(ring);
      const cy = center?.lat ?? (hasPoint ? lat : NaN);
      const cx = center?.lng ?? (hasPoint ? lng : NaN);
      if (cy == null || cx == null || Number.isNaN(cy) || Number.isNaN(cx)) {
        return false;
      }

      const recurring = isRecurringAnomalyDetail(ap);
      lc.addDynamicPolygon(ring, {
        id: 'anomaly-highlight',
        color,
        strokeColor: color,
        strokeWidth: recurring ? 4 : hasPolygon ? 3 : 2,
        dashArray: recurring ? ANOMALY_RECURRING_STROKE_DASH : undefined,
        fillColor: color,
        fill: true,
        fillOpacity: recurring ? 0.3 : 0.38,
        opacity: 1,
        interactive: false,
        pane: 'anomalyHighlightPane',
      });

      const ringBounds = latLngRingToBounds(ring);
      this.pendingFocusBounds = ringBounds ? padLatLngBounds(ringBounds, 2) : null;
      this.ignoreNextZoomReload = true;
      this.mapZoom = BID_MAP_ZOOM_DETAIL_MIN;
      this.previousZoomBand = 'detail';
      lc.centerToPoint(cy, cx, BID_MAP_ZOOM_DETAIL_MIN, false);
      this.refreshMapData();
      return true;
    } catch {
      return false;
    }
  }

  private loadRegionsOnce(): void {
    if (this.regionsLoaded) {
      return;
    }
    const osm = this.openstreetmapComponent;
    this.setOsmLoading(true);
    this.regionManager
      .toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: polygons => {
          this.setOsmLoading(false);
          this.regionsLoaded = true;
          if (polygons?.length) {
            this.regionManager.configurePolygonEvents(polygons, this.mapOptions, undefined);
            this.attachRegionPathRefreshOnHover(polygons);
            osm.mapController$.next({addGisPolygons: polygons});
            setTimeout(() => {
              this.mapOptions.showRegionNames = true;
              this.toggleRegionNames();
              this.refreshRegionPathDisplay();
            });
            this.regionPathDisplay = this.getRegionPathString();
          }
          this.scheduleReload(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.setOsmLoading(false);
          this.scheduleReload(false);
          this.cdr.markForCheck();
        },
      });
  }

  private bindMapViewportEvents(): void {
    const osm = this.openstreetmapComponent;
    if (!osm?.mapZoomChange$ || !osm?.mapDragEnd$) {
      return;
    }
    this.mapViewportEventsBound = true;
    osm.mapZoomChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(zoom => {
        this.mapZoom = zoom;
        const band = bidMapZoomBand(zoom);
        const prevBand = this.previousZoomBand;
        if (this.ignoreNextZoomReload) {
          this.ignoreNextZoomReload = false;
          this.previousZoomBand = band;
          return;
        }

        if (band === 'agg') {
          if (prevBand !== 'agg') {
            this.previousZoomBand = band;
            this.scheduleReload(false);
          } else {
            this.previousZoomBand = band;
          }
          return;
        }

        this.previousZoomBand = band;
        this.scheduleReload(false);
      });
    osm.mapDragEnd$
      .pipe(takeUntil(this.destroy$), debounceTime(400))
      .subscribe((evt: {zoom?: number}) => {
        const z = evt?.zoom ?? this.mapZoom;
        const band = bidMapZoomBand(z);
        if (band === 'cluster' || band === 'detail') {
          this.scheduleReload(false);
        }
      });
  }

  private scheduleReload(_debounced: boolean): void {
    queueMicrotask(() => this.refreshMapData());
  }

  private syncZoomFromMap(): void {
    try {
      const z = this.openstreetmapComponent?.getMapInstance?.()?.getZoom?.();
      if (typeof z === 'number' && !Number.isNaN(z)) {
        this.mapZoom = z;
        this.previousZoomBand = bidMapZoomBand(z);
      }
    } catch {
      /* ignore */
    }
  }

  private refreshMapData(): void {
    const osm = this.openstreetmapComponent;
    if (!osm?.isMapInitialized) {
      return;
    }

    this.cancelRequest$.next();
    this.clearAnomalyLayers(osm);

    const band = bidMapZoomBand(this.mapZoom);
    const filters = [...(this.filtersParams ?? [])];
    this.maybeRefreshAnomaliesCount(filters);

    const focusBounds = this.pendingFocusBounds;
    if (focusBounds) {
      this.pendingFocusBounds = null;
      this.loadDetails(osm, focusBounds, filters);
      return;
    }

    if (band === 'agg') {
      this.loadAggregates(osm, filters);
      return;
    }

    const bounds = getBidMapBounds(osm);
    if (!bounds) {
      return;
    }

    if (band === 'cluster') {
      this.loadClusters(osm, bounds, filters);
      return;
    }

    this.loadDetails(osm, bounds, filters);
  }

  private maybeRefreshAnomaliesCount(filters: FieldFilter[]): void {
    const hash = JSON.stringify(filters ?? []);
    if (hash === this.lastCountFiltersHash || hash === this.pendingCountFiltersHash) {
      return;
    }
    this.pendingCountFiltersHash = hash;
    this.anomalyMapManager
      .loadTotalCount(filters)
      .pipe(takeUntil(this.mapViewportCancel$))
      .subscribe({
        next: count => {
          this.lastCountFiltersHash = hash;
          this.pendingCountFiltersHash = '';
          this.anomaliesCount = Number.isFinite(count) ? count : 0;
          this.cdr.markForCheck();
        },
        error: () => {
          this.lastCountFiltersHash = hash;
          this.pendingCountFiltersHash = '';
          this.anomaliesCount = 0;
          this.cdr.markForCheck();
        },
        complete: () => {
          if (this.pendingCountFiltersHash === hash) {
            this.pendingCountFiltersHash = '';
          }
        },
      });
  }

  private loadAggregates(osm: OpenStreetMapComponent, filters: FieldFilter[]): void {
    this.setOsmLoading(true);
    this.anomalyMapManager
      .loadRegionAggregates(filters, osm)
      .pipe(
        takeUntil(this.mapViewportCancel$),
        finalize(() => {
          this.setOsmLoading(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: circles => {
          if (circles.length) {
            osm.mapController$.next({addCircleMarkers: circles});
          }
        },
      });
  }

  private loadClusters(
    osm: OpenStreetMapComponent,
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: FieldFilter[],
  ): void {
    this.setOsmLoading(true);
    this.anomalyMapManager
      .loadClusters(bounds, filters, osm)
      .pipe(
        takeUntil(this.mapViewportCancel$),
        finalize(() => {
          this.setOsmLoading(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({circles, pointMarkers}) => {
          const markers = [...pointMarkers];
          if (circles.length) {
            osm.mapController$.next({addCircleMarkers: circles});
          }
          if (markers.length) {
            this.anomalyMarkerIds = markers.map(m => m.id);
            osm.mapController$.next({mergeMarkers: markers});
          }
        },
      });
  }

  private loadDetails(
    osm: OpenStreetMapComponent,
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: FieldFilter[],
  ): void {
    this.setOsmLoading(true);
    this.anomalyMapManager
      .loadDetails(bounds, filters)
      .pipe(
        takeUntil(this.mapViewportCancel$),
        switchMap(dto =>
          this.anomalyMapManager.loadFieldOutlinePolygonsForAnomalyDetails(dto).pipe(
            map(fieldOutlines => ({dto, fieldOutlines})),
          ),
        ),
        finalize(() => {
          this.setOsmLoading(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({dto, fieldOutlines}) => {
          const {polygons, markers} = this.anomalyMapManager.detailsToLayers(dto, (clickedDto) =>
            this.anomalyDateSync.onAnomalyClicked(clickedDto),
          );
          const mergedPolygons = [...fieldOutlines, ...polygons];
          if (mergedPolygons.length) {
            this.anomalyPolygonIds = mergedPolygons.map(p => String(p.id));
            osm.mapController$.next({addGisPolygons: mergedPolygons});
          }
          if (markers.length) {
            this.anomalyMarkerIds = markers.map(m => m.id);
            osm.mapController$.next({mergeMarkers: markers});
          }
          this.tryFocusLoadedAnomaly(osm);
        },
      });
  }

  private tryFocusLoadedAnomaly(osm: OpenStreetMapComponent): void {
    const targetId = this.pendingFocusedAnomalyId;
    if (!targetId) {
      return;
    }
    const lc = osm.getLayersController();
    if (!lc) {
      return;
    }
    const allPolygons = lc.getAllPolygons?.() ?? [];
    const match = allPolygons.find((p: any) => {
      const data = p?.properties?.data;
      const candidate = this.readAnomalyId(data?.anomalyPolygon ?? data);
      return !!candidate && candidate === targetId;
    });
    if (!match?.polygon) {
      return;
    }
    try {
      if (typeof match.polygon.bringToFront === 'function') {
        match.polygon.bringToFront();
      }
      if (typeof match.polygon.openPopup === 'function') {
        match.polygon.openPopup();
      }
      this.pendingFocusedAnomalyId = null;
    } catch {
      /* ignore */
    }
  }

  /**
   * Preserve currently opened anomaly popup across language refresh.
   * `detailsToLayers` rebuilds popup HTML with translated labels, so reloading + refocus updates the content.
   */
  private captureOpenedAnomalyPopup(): void {
    const osm = this.openstreetmapComponent;
    const lc = osm?.getLayersController?.();
    if (!lc?.getAllPolygons) {
      return;
    }
    const allPolygons = lc.getAllPolygons() ?? [];
    for (const p of allPolygons) {
      const shape = p?.polygon;
      if (!shape || typeof shape.isPopupOpen !== 'function' || !shape.isPopupOpen()) {
        continue;
      }
      const data = p?.properties?.data;
      const id = this.readAnomalyId(data?.anomalyPolygon ?? data);
      if (id) {
        this.pendingFocusedAnomalyId = id;
        return;
      }
    }
  }

  private readAnomalyId(source: any): string | null {
    const raw = source?.id ?? source?.anomalyId;
    if (raw == null) {
      return null;
    }
    const id = String(raw).trim();
    return id ? id : null;
  }

  private clearAnomalyLayers(osm: OpenStreetMapComponent): void {
    if (this.anomalyMarkerIds.length) {
      osm.mapController$.next({removeMarkers: [...this.anomalyMarkerIds]});
    }
    if (this.anomalyPolygonIds.length) {
      osm.mapController$.next({removePolygons: [...this.anomalyPolygonIds]});
    }
    osm.mapController$.next({clearCircleMarkers: true});
    this.anomalyMarkerIds = [];
    this.anomalyPolygonIds = [];
  }

  private setOsmLoading(loading: boolean): void {
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = loading;
    }
  }

  private toggleRegionNames(): void {
    this.mapController$.next({
      setShowRegionNames: this.mapOptions.showRegionNames,
    });
  }

  private getRegionPathString(): string {
    return this.regionManager.getRegionPathString(
      this.translate.instant(environment.country),
      this.translate.instant('ALL_REGIONS'),
    );
  }

  private attachRegionPathRefreshOnHover(polygons: GisPolygon[]): void {
    for (const polygon of polygons) {
      const prevHover = polygon.onHover;
      const prevHoverOut = polygon.onHoverOut;
      polygon.onHover = (poly: GisPolygon) => {
        prevHover?.(poly);
        this.refreshRegionPathDisplay();
      };
      polygon.onHoverOut = (poly: GisPolygon) => {
        prevHoverOut?.(poly);
        this.refreshRegionPathDisplay();
      };
    }
  }

  private refreshRegionPathDisplay(): void {
    this.ngZone.run(() => {
      this.regionPathDisplay = this.getRegionPathString();
      this.cdr.markForCheck();
    });
  }
}
